import React, { useCallback, useMemo, useState } from 'react';
import css from './styles.module.scss';
import { Typography, IconButton, Popover, List, ListItem, ListItemText, Grid, Box } from '@material-ui/core';
import classNames from 'classnames';
import { useAppRoute } from '~/utils/hooks/useAppRoute';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { MusicListItemDto } from '~/models/music/music.dto';
import { useDispatch } from 'react-redux';
import { MusicApi } from '~/api/music.api';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { isSongFormEditing } from '~/features/dashboard/store/actions';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { DateFormat } from '~/common/date-format';
import Avatar from '@material-ui/core/Avatar';
import AvatarGroup from '@material-ui/lab/AvatarGroup';
import Checkbox from '@material-ui/core/Checkbox';
import {orderBy} from "lodash";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        popover: {
            pointerEvents: 'none',
        },
        popoverContent: {
            pointerEvents: 'auto',
        },
        root: {
            width: '100%',
            maxWidth: 360,
            backgroundColor: theme.palette.background.paper,
        },
    })
);

type DealCardProps = {
    data: MusicListItemDto;
    onChange?(isChecked: boolean): void;
    className?: string;
};

export const SongCard: React.FC<DealCardProps> = ({ data, onChange, className }) => {
    const route = useAppRoute();
    const classes = useStyles();
    const match = useRouteMatch<{ creatorId: string }>();
    const dispatch = useDispatch();
    const soundDetailsRoute = useMemo(() => `${match.url}/${data.assetCode}/details`, [match.url, data.assetCode]);
    const goToSoundDetails = useCallback(() => route(soundDetailsRoute), [soundDetailsRoute, route]);

    const history = useHistory();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const togglePopover = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    const open = Boolean(anchorEl);

    const [checked, setChecked] = useState(data.dealSongLinkFlag === 1);
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = !!event.target.checked;
        setChecked(value);
        onChange && onChange(value);
    };

    const editDealForm = (id: any) => {
        dispatch(isSongFormEditing(true));
        history.push({
            pathname: `/creators-list/${match.params.creatorId}/dashboard/songs/add-song/${id}`,
            state: {
                gotoStep: '',
            },
        });
    };

    const handleDownloadClick = async (id: any) => {
        MusicApi.exportMusicDetails(id).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'].split('; ')[0] }));
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.download = 'download.zip';
            link.click();
            window.URL.revokeObjectURL(url);
            link.remove();
        });
    };

    const songDetailsRoute = useMemo(() => `${match.url}/${data.assetCode}/details`, [match.url, data.assetCode]);

    const goToSongDetails = useCallback(() => route(songDetailsRoute), [songDetailsRoute, route]);
    const songInfo = orderBy(data.songProjectTrack, ['releaseDate'], ['asc']);

    return (
        <Box className={classNames(css.module, className)}>
            <Grid container className={css.topSection} spacing={2}>
                <Grid item xs={7}>
                    <div className={css.nameSection}>
                        <AvatarGroup onClick={goToSoundDetails} max={6}>
                            <Avatar
                                src={process.env.REACT_APP_ASSET + data.songArtistImage}
                                style={{ marginRight: '10px' }}
                                className={classNames(css.iconColor, css.iconBg)}
                            />
                        </AvatarGroup>
                        <Typography className={css.songName}>{data.songName}</Typography>
                    </div>
                </Grid>
                <Grid item xs={5}>
                    <div className={css.iconBox}>
                        <Typography className="releasedate ">Release Date</Typography>
                        {data.songProjectTrack.length > 0 && songInfo[0].releaseDate ? (
                            <Typography className="gray-text">
                                {' '}
                                <DateFormat newDate={songInfo[0].releaseDate} />{' '}
                            </Typography>
                        ) : (
                            <Typography className="gray-text ">MISSING</Typography>
                        )}
                    </div>
                </Grid>
                <div className={css.typography_customright}>
                    {onChange ? (
                        <Checkbox checked={checked} onChange={handleChange} inputProps={{ 'aria-label': 'primary checkbox' }} style={{ cursor: 'pointer' }} />
                    ) : (
                        <>
                            <IconButton
                                aria-label="add to favorites"
                                aria-owns={open ? 'mouse-over-popover' : undefined}
                                aria-haspopup="true"
                                onClick={togglePopover}
                            >
                                <MoreVertIcon />
                            </IconButton>

                            <Popover
                                id="mouse-over-popover"
                                className={classes.popover}
                                classes={{
                                    paper: classes.popoverContent,
                                }}
                                open={open}
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                onClose={togglePopover}
                                disableRestoreFocus
                                PaperProps={{ onMouseLeave: togglePopover }}
                            >
                                <List component="nav" className={classes.root} aria-label="contacts">
                                    <ListItem button onClick={goToSongDetails}>
                                        <ListItemText primary="View" />
                                    </ListItem>
                                    <ListItem button onClick={() => editDealForm(data.assetCode)}>
                                        <ListItemText primary="Edit" />
                                    </ListItem>
                                    <ListItem button onClick={() => handleDownloadClick(data.assetCode)}>
                                        <ListItemText primary="Excel Export" />
                                    </ListItem>
                                </List>
                            </Popover>
                        </>
                    )}
                </div>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={7}>
                    <Typography className="artist-title">Artist</Typography>
                    <Typography className="gray-text">{songInfo[0]?.trackArtist ? songInfo[0]?.trackArtist : 'MISSING'}</Typography>
                </Grid>
                <Grid item xs={5}>
                    <Typography className="releasedate">Writing Share</Typography>
                    <Typography className="gray-text">{!data.writingShare || data.writingShare === 999 ? 'MISSING' : `${data.writingShare}%`}</Typography>
                </Grid>
            </Grid>
        </Box>
    );
};
